import type { CompRow, Recommendation } from "../types/recommendation";
import { ProvenanceChip } from "../components/ProvenanceChip";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { blockStyle } from "../ui/Block";

/**
 * The subject-analysis result screen — first `/next` screen built (ADR-0003
 * workstream 7), wired to render the SAME `Recommendation` contract across
 * all three verticals (protest / BPO / appraisal support). A PURE renderer:
 * every figure comes from `recommendation.fields[...]` exactly as the
 * engine produced it; this component performs no arithmetic and derives no
 * tier — it only lays the contract out (ADR-0003 §4, project strict tier).
 *
 * Definition-of-done self-check (per the project's own gate, mirrored from
 * `valubrief-backend-scaffold.md`'s `definitionOfDone`): a conclusion figure
 * with a citation chip is present; the disclosed-not-relied section is
 * rendered prominently, not buried; the evidence index is labeled 75/25
 * support-vs-disclosed with its "not a probability" note attached in the
 * same breath; every comp shows both why-it-fits and why-it-may-not.
 */
export function ResultScreen({ reco }: { reco: Recommendation }) {
  const primaryFieldId = pickPrimaryFieldId(reco);
  // VB-39: a static name, deliberately. Naming the tab after the subject would be better — several
  // briefs open at once is the normal case — but `Recommendation` carries only `subjectId`
  // ("subj_mock_0001"), an opaque key, and no display address. Putting that key in a tab title is
  // worse than a generic name, and adding an address to the contract is a contract decision, not a
  // title fix. Recorded here rather than guessed at.
  useDocumentTitle(vbTitle("Source-traced result"));
  const primary = primaryFieldId ? reco.fields[primaryFieldId] : undefined;

  return (
    <main
      style={{
        ...blockStyle("prose"),
        margin: "0 auto",
        padding: "32px 20px 80px",
        fontFamily: typeTokens.fontUi,
        color: colors.textPrimary,
        background: colors.bgApp,
      }}
    >
      {/* Verdict band */}
      <section style={{ background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
        <p className="vb-sec-label">
          Indicated conclusion
        </p>
        <div style={{ marginTop: 8, fontSize: 30, fontWeight: 600 }}>
          {primary ? <ProvenanceChip field={primary} /> : <span style={{ color: colors.textSecondary }}>—</span>}
        </div>
        <p style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary }}>
          Scenario: {reco.scenario} · generated {new Date(reco.generatedAt).toLocaleDateString()} · pipeline{" "}
          {reco.pipelineVersion}
        </p>
      </section>

      {/* Evidence-support index — 75/25 framing, never a win-probability (locked decision). */}
      <section style={{ marginTop: 20 }}>
        <EvidenceIndexBar support={reco.evidenceIndex.support} disclosed={reco.evidenceIndex.disclosed} />
      </section>

      {/* Disclosed-not-relied — must be impossible to miss (definition of done). */}
      {reco.disclosed.length > 0 && (
        <section
          style={{
            marginTop: 20,
            borderLeft: `4px solid ${colors.caution}`,
            background: colors.cautionBg,
            borderRadius: 8,
            padding: 18,
          }}
        >
          <h2 style={{ fontSize: 15, margin: 0, color: colors.caution }}>Disclosed, not relied on</h2>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}>
            Evidence the analysis considered and set aside — shown so the record is never silent about it.
          </p>
          <ul style={{ marginTop: 10, paddingLeft: 18 }}>
            {reco.disclosed.map((d, i) => (
              <li key={i} style={{ marginBottom: 8, fontSize: 14 }}>
                <strong>{d.name}</strong> — {d.metric} ({d.kind}). {d.reason}
                <div style={{ fontSize: 12, color: colors.textSecondary }}>{d.source}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Gaps — unknown is never zero. */}
      {reco.gaps.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 15 }}>Not found</h2>
          <ul style={{ paddingLeft: 18 }}>
            {reco.gaps.map((g, i) => (
              <li key={i} style={{ fontSize: 14, color: colors.textSecondary, marginBottom: 4 }}>
                {g.label ?? g.ref ?? "Value"} — <span style={{ color: colors.textPrimary }}>{g.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Sales comps */}
      {reco.salesComps.length > 0 && <CompTable title="Sales comps" rows={reco.salesComps} />}

      {/* Rent comps */}
      {reco.rentComps.length > 0 && <CompTable title="Rent comps" rows={reco.rentComps} />}

      {/* Every OTHER field, for completeness of the trace — the primary figure
          already has its own chip in the verdict band above, so it's excluded
          here rather than shown twice. */}
      <section style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 15 }}>All figures in this analysis</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 10 }}>
          {Object.values(reco.fields)
            .filter((f) => f.id !== primaryFieldId)
            .map((f) => (
            <div
              key={f.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 14px",
                background: colors.bgSurface,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 14, color: colors.textSecondary }}>{f.label}</span>
              <ProvenanceChip field={f} />
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/** Prefers a field literally named "indicated_value" (this session's mock
 * convention) but falls back to the highest-tier verified field so the
 * screen never renders a hard-coded, vertical-specific key name. Real
 * wiring at cutover should have the engine mark ONE field as the primary
 * conclusion explicitly rather than relying on this heuristic — flagged
 * here as a placeholder, not a design decision. */
function pickPrimaryFieldId(reco: Recommendation): string | undefined {
  if (reco.fields["indicated_value"]) return "indicated_value";
  const verified = Object.values(reco.fields).find((f) => f.tier === "verified");
  return verified?.id;
}

function EvidenceIndexBar({ support, disclosed }: { support: number; disclosed: number }) {
  return (
    <div style={{ background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: colors.textSecondary }}>
        <span>Evidence support</span>
        <span>
          {support}% support / {disclosed}% disclosed
        </span>
      </div>
      <div
        style={{ marginTop: 8, height: 8, borderRadius: 4, background: colors.bgApp, overflow: "hidden", display: "flex" }}
        role="img"
        aria-label={`${support} percent supporting evidence, ${disclosed} percent disclosed-not-relied evidence`}
      >
        <div style={{ width: `${support}%`, background: colors.verified }} />
        <div style={{ width: `${disclosed}%`, background: colors.caution }} />
      </div>
      <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>
        This is a share of weighted evidence, not a probability of any outcome.
      </p>
    </div>
  );
}

function CompTable({ title, rows }: { title: string; rows: CompRow[] }) {
  return (
    <section style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 15 }}>{title}</h2>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 13, minWidth: 640 }}>
          <thead>
            <tr style={{ textAlign: "left", color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
              <th style={{ padding: "6px 8px" }}>#</th>
              <th style={{ padding: "6px 8px" }}>Address</th>
              <th style={{ padding: "6px 8px" }}>SF</th>
              <th style={{ padding: "6px 8px" }}>Match</th>
              <th style={{ padding: "6px 8px" }}>Why it fits</th>
              <th style={{ padding: "6px 8px" }}>Why it may not</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rank} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: "8px" }}>{r.rank}</td>
                <td style={{ padding: "8px" }}>{r.addr}</td>
                <td style={{ padding: "8px", fontFamily: "monospace" }}>{r.sf}</td>
                <td style={{ padding: "8px" }}>{r.match}/100 evidentiary match</td>
                <td style={{ padding: "8px" }}>{r.good}</td>
                <td style={{ padding: "8px" }}>{r.notnot}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
